import React, { useContext, useEffect, useState } from 'react';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import { useParams } from 'react-router-dom';
import { addSuffix, formatCurrency, formatPhone, generateId, inputDate, shortHandDate, statusColors } from '../../utility/functions';
import Col from '../../layout/Col';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import FormSetting from '../../layout/FormSetting';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import PageBreak from '../../layout/PageBreak';
import Row from '../../layout/Row';
import useDebounce from '../../utility/debounce';
import WCSignUp, { BillingStartDate, getBSD } from '../../processors/WCSignUp';
import { accountTypes } from '.';
import RoleProtected, { IsAuthorized, userRoles } from '../../RoleProtected';
import Table from '../../table/Table';
import LinkTo from '../../forms/LinkTo';
import Dot from '../../Dot';
import AddNewCustomer from '../../processors/AddNewCustomer';
import Loading from '../../Loading';
import { UserContexts } from '../../stateProvider/UsersContext';
import Heading from '../../forms/Heading';
import FeatureTOS from '../../FeatureTOS';
import ModalFactory from '../../Modal';
import WashClubSettings from './WashClubSettings';
import AllowedFeatures, { featureNames } from '../../navigation/AllowedFeatures';
import WashClubFraud from './WashClubFraud';

const WashClubManagement = ({ ...props }) => {
    // const {
    //     state: {

    //     },
    // } = useGlobalState();

    const { guid } = useParams()

    const context = useContext(UserContexts);

    const betaTester = IsAuthorized(userRoles.betaTester);

    if (!context.washClub) return <Loading page />;

    const { user, washClub } = context;

    const isInAClub = washClub.status === 'Active' || washClub.status === 'EndBillingPending';

    const releaseDeviceId = () => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Release Device ID?`,
                body: (<span>Are you sure you want to release {user.firstName}'s device ID?</span>),
                type: 'warning',
            },
            promise: {
                title: 'Releasing Device ID',
                body: `Releasing, please wait.`,
                callback: () => HamQuest.delete(QuestRoute.user.deviceId(guid))
            },
            confirmed: {
                title: 'Success',
                body: `${user.firstName}'s Device released.`,
                type: 'success'
            },
        })
    }

    const removeFamilyMember = (user) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Remove ${user.firstName}?`,
                body: (<span>Are you sure you want to remove {user.fullName} from the family plan?</span>),
                type: 'warning',
            },
            promise: {
                title: 'Removing from the plan',
                body: `Removing, please wait.`,
                callback: () => HamQuest.get(QuestRoute.user.removeFromPlan(user.customerId))
            },
            confirmed: {
                title: 'Success',
                body: `${user.fullName}'s successfully removed.`,
                type: 'success'
            },
            finally: () => context.refreshWC()
        })
    }

    const addFamilyMember = (context) => {

        const FamForm = ({ ...props }) => {
            const [query, setQuery] = useState('')
            const [greaterThan, setGreaterThan] = useState(false)
            const [searched, setSearched] = useState([])
            const debounced = useDebounce(query, 400)

            useEffect(() => {
                if (debounced.length < 2) {
                    setSearched([])
                    setGreaterThan(false)
                    return
                }

                HamQuest.getQuery(QuestRoute.users, { name: debounced, parentId: guid })
                    .then(res => {
                        res = res.filter(e => e.userStatus === 'Active')
                        if (res.length > 5) {
                            const firstFiveResults = res.slice(0, 5);
                            setSearched(firstFiveResults);
                            setGreaterThan(true);
                        } else {
                            setGreaterThan(false);
                            setSearched(res);
                        }
                    })
            }, [debounced])

            return (
                <Col>
                    <Input
                        label='Add User to Plan'
                        onInput={e => setQuery(e.target.value)}
                        value={query}
                        standalone
                        placeholder='Search for User'
                        flex='0'
                    />
                    <Col>
                        {searched.map(e => (
                            <Row key={generateId()} style={{ justifyContent: 'space-between' }}>
                                <span>{e.fullName}</span>{e.accountType === accountTypes.standard ? <WCSignUp context={context} child={e} /> : 'In a Family Plan'}
                            </Row>
                        ))}
                        {greaterThan ? "..." : null}
                    </Col>
                </Col>
            )
        }

        ModalFactory.show({
            title: 'Add to Family Plan',
            body: <FamForm />
        })
    }

    const currentBillingAmount = () => {
    return formatCurrency(washClub.billingAmount + 
        (washClub.children ? 
            washClub.children.reduce((a, b) => a += (b.clubStatus === 'Active' ? parseFloat(b.billingAmount || 0) : 0), 0) 
            : 0));
}

    return (
        <PageBreak>
            <FormSetting
                name='userWashClubSettings'
                label='Management'
                inputs={{
                    ...washClub,
                    billingStartDate: inputDate(getBSD(washClub.billingDay, washClub.billingStartDate)),
                }}
                controls={[
                    isInAClub ? <Input
                        label='Current Billing Amount'
                        value={currentBillingAmount()}
                        readOnly
                        standalone
                    /> : null,
                    <RoleProtected roles={userRoles.editCustomers}>
                        <WCSignUp key={'WCSignUp1'} />
                    </RoleProtected>,
                    <Button
                        key={'ReleaseDeviceID'}
                        roles={userRoles.editCustomers}
                        onClick={releaseDeviceId}
                        label='Release Device ID'
                    />,

                ]}
                onSubmit={async e => {
                    var response = true;
                    if ((e.status === 'Inactive' || e.status === 'EndBillingPending') && user.accountType === accountTypes.parent) {
                        response = await ModalFactory.confirmation({
                            title: 'Are you Sure?',
                            body: 'Setting the parent Account to ' + e.status + ' will also set child accounts to the same status.',
                            type: 'warning',
                            closeAfter: true,
                        })
                    } 
                    if(response) {
                        return HamQuest.put(QuestRoute.user.washClub(guid), { ...e }).then(() => context.refreshWC())
                    }
                    else {
                        const canceledForm = { type: 'Canceled' }
                        throw canceledForm
                   }
                }}
                text='Update'
                editable
                displayForm={washClub.status === 'Inactive' || !washClub.status}
                roles={userRoles.editCustomers}
            >
                {({ values, setValue, editing }) => {
                    if (values.status === undefined) values.status = 'Inactive';

                    const statusOptions = HSOptions.washClub.status;

                    if (washClub.status === 'CreditHold') {
                        statusOptions.forEach(e => {
                            e.disabled = e.value !== 'Inactive'
                        })
                    }

                    return (
                        <Col>
                            <Select
                                label='Wash Club Status'
                                name='status'
                                options={statusOptions}
                                required
                            />

                            {
                                values.status !== 'Inactive' ?
                                    <>
                                        <Select
                                            label='Wash Club'
                                            name='productId'
                                            getOptions={QuestRoute.washClubs.clubs}
                                            buildOptions={(e) => {
                                                e.label = e.productName;
                                                e.value = e.productId;
                                            }}
                                            required
                                        />
                                    </> : null
                            }

                            {user.accountType !== accountTypes.child ? (
                                <Row wrap>
                                    <Input
                                        label='Billing Day'
                                        name='billingDay'
                                        value={addSuffix(values.billingDay)}
                                        disabled
                                        standalone
                                    />
                                    {(values.status === 'Active' || values.status === 'EndBillingPending') && (
                                        <BillingStartDate values={values} setValue={setValue} editing={editing} />
                                    )}
                                </Row>
                            ) : null}
                        </Col>
                    )
                }}
            </FormSetting>
            
            {/* {isInAClub ? ( */}
                <PageBreak>
                    <WashClubSettings />
                </PageBreak>
            {/* ) : null} */}

            {betaTester ? (user.accountType !== accountTypes.child) && (
                <FeatureTOS feature='familyPlanningTOS'>
                    <Table
                        config={{
                            label: 'Family Plan Management',
                            disableHeaderSearch: true,
                            data: washClub.children || [],
                            controls: [
                                <Button
                                    roles={userRoles.editCustomers}
                                    onClick={() => addFamilyMember(context)}
                                    label='Add Family Member'
                                    disabled={washClub.status !== 'Active'}
                                />,
                                <Button
                                    roles={userRoles.editCustomers}
                                    onClick={() => {
                                        const id = generateId();
                                        ModalFactory.show({
                                            id,
                                            title: `New Customer`,
                                            body: <AddNewCustomer modalId={id} isChild context={context} />
                                        })
                                    }}
                                    label='New Customer'
                                    disabled={washClub.status !== 'Active'}
                                />
                            ],
                            headers: [
                                {
                                    label: 'Name',
                                    propKey: 'fullName',
                                }, {
                                    label: 'Email',
                                    propKey: 'email',
                                }, {
                                    label: 'Phone Number',
                                    propKey: 'cellPhoneNumber',
                                }, {
                                    label: 'Status',
                                    propKey: 'userStatus',
                                    options: true
                                }, {
                                    label: 'Last Login',
                                    propKey: 'lastLoginDate',
                                    type: 'date',
                                    sortBy: true
                                }, {
                                    label: 'Wash Club',
                                    propKey: 'awardName',
                                    options: true
                                }, {
                                    label: 'Club Status',
                                    propKey: 'clubStatus',
                                    options: true
                                },
                                // {
                                //     label: 'Media',
                                //     type: 'string',
                                //     propKey: 'mediaKey',
                                // },
                                {
                                    label: 'Management',
                                },
                            ],
                            renderFormat: (e) => (
                                <tr key={generateId()}>
                                    <td>
                                        <LinkTo customerId={e.userId}>{e.fullName}</LinkTo>
                                    </td>
                                    <td>{e.email}</td>
                                    <td>{formatPhone(e.cellPhoneNumber) || '-'}</td>
                                    <td>
                                        <Row>
                                            <Dot color={statusColors[e.userStatus]} />
                                            <span>
                                                {e.userStatus}
                                            </span>
                                        </Row>
                                    </td>
                                    <td>{shortHandDate(e.lastLoginDate)}</td>
                                    <td>{e.awardName}</td>
                                    <td>
                                        <Row>
                                            <Dot color={statusColors[e.clubStatus]} />
                                            <span>
                                                {e.clubStatus}
                                            </span>
                                        </Row>
                                    </td>
                                    <td>
                                        <Button onClick={() => removeFamilyMember(e)}>Remove</Button>
                                    </td>
                                </tr>
                            )
                        }}
                    />
                </FeatureTOS>
            ) : (
                <Col>
                    <Heading>Family Planning</Heading>
                    <span>Coming soon, please submit a Feedback form to sign up for testing!</span>
                </Col>
            )}

        </PageBreak>
    );
}

export default WashClubManagement;
